import { MRT_ColumnDef } from "material-react-table";

// `dash` and `formatDate` now come from utils/missing, which owns all six
// missing-data states. Re-exported here so existing table imports keep working.
// The old `dash` was a katakana mark in a hard-coded light grey (1.9:1);
// `absent` is an en dash in tokens.color.faint, which clears AA.
export { absent as dash, formatDate, formatDateOnly } from "./missing";

/**
 * Spread into a table's `muiFilterTextFieldProps`. Password managers classify
 * MRT's "Filter by Email" / "Filter by Full Name" inputs as login fields and
 * autofill them right after sign-in — the filter then matches nothing and the
 * table reads "No results found" until a remount clears it (seen live on the
 * System Management user list, 2026-08-03). autoComplete alone is not enough;
 * the data- attributes are the documented opt-outs for LastPass, 1Password and
 * Dashlane respectively.
 */
export const filterAutofillGuard = {
  autoComplete: "off",
  inputProps: {
    autoComplete: "off",
    "data-lpignore": "true",
    "data-1p-ignore": "true",
    "data-form-type": "other",
  },
} as const;

// Key-order-insensitive stringify; saved JSON and live state compare equal
export const stableStringify = (obj: any): string =>
  JSON.stringify(obj, (_, v) =>
    v && typeof v === "object" && !Array.isArray(v)
      ? Object.keys(v).sort().reduce((acc: any, k) => { acc[k] = v[k]; return acc; }, {})
      : v
  );

// Default order from the column defs; prepends display columns (e.g. "mrt-row-expand")
export const getDefaultTableColumnOrder = (
  columns: MRT_ColumnDef<any>[],
  leadingIds: string[] = []
): string[] =>
  [...leadingIds, ...columns.map((c) => (c.accessorKey ?? c.id) as string)];

// Default sizing from each def's `size`; resizing back to it reads as not-dirty;
// a reset restores the designed widths
export const getDefaultTableColumnSizing = (columns: MRT_ColumnDef<any>[]): Record<string, number> =>
  columns.reduce((acc: Record<string, number>, c) => {
    if (c.size !== undefined) acc[(c.accessorKey ?? c.id) as string] = c.size;
    return acc;
  }, {});

// Fit `order` to the live columns; drops dead ids (e.g. role-gated columns);
// inserts new ids at their default spot; keeps the user's relative ordering
export const resetTableColumnOrder = (order: string[], defaultColumnOrder: string[]): string[] => {
  const merged = (order ?? []).filter((id) => defaultColumnOrder.includes(id));
  defaultColumnOrder.forEach((id, i) => {
    if (merged.includes(id)) return;
    const neighbor = defaultColumnOrder.slice(0, i).reverse().find((n) => merged.includes(n));
    merged.splice(neighbor ? merged.indexOf(neighbor) + 1 : 0, 0, id);
  });
  return merged;
};

// Drops sizing for dead columns; fills gaps from defaults
export const resetTableColumnSizing = (
  sizing: Record<string, number>,
  defaultColumnOrder: string[],
  defaultColumnSizing: Record<string, number>
): Record<string, number> =>
  Object.entries(sizing ?? {})
    .filter(([id]) => defaultColumnOrder.includes(id))
    .reduce((acc, [id, size]) => { acc[id] = size; return acc; }, { ...defaultColumnSizing });

// Normalizes a saved settings blob against the live columns; {} yields pure defaults
export const resetTableSettings = (
  s: any,
  defaultColumnOrder: string[],
  defaultColumnSizing: Record<string, number>
) => ({
  columnOrder: resetTableColumnOrder(s?.columnOrder ?? [], defaultColumnOrder),
  sorting: s?.sorting ?? [],
  columnVisibility: s?.columnVisibility ?? {},
  columnSizing: resetTableColumnSizing(
    (s?.columnSizing ?? {}) as Record<string, number>,
    defaultColumnOrder,
    defaultColumnSizing
  ),
  density: s?.density ?? "comfortable",
});
